import axios from "axios";
import { useCallback, useRef } from "react";
import { useLogin } from "../login";
import { environment } from "../../environment";
import { errorCatcher } from "../../hooks";
import {
    ConnectionStatusResponse,
    PromptConfigResponse,
    SlotImageUploadResponse,
    SocialPostGeneration,
    SocialPromptImageSlot,
    SocialSlotRecentImage
} from "./types";

const BASE = () => `${environment.v2Url}/social-publishing`;

// #region useSocialPublishing
export const useSocialPublishing = () => {
    const [, authToken, loginFunctions] = useLogin();

    const authTokenRef = useRef(authToken);
    authTokenRef.current = authToken;
    const logoutRef = useRef(loginFunctions.logout);
    logoutRef.current = loginFunctions.logout;

    const headers = useCallback(() => ({ "X-Wallmates-Auth": authTokenRef.current || '' }), []);

    // #region getConnectionStatus
    const getConnectionStatus = useCallback(async (brandId: number): Promise<ConnectionStatusResponse | null> => {
        try {
            const res = await axios.get(`${BASE()}/connection/${brandId}`, { headers: headers() });
            return res.data as ConnectionStatusResponse;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            return null;
        }
    }, []);

    // #region getConnectUrl
    const getConnectUrl = useCallback(async (brandId: number): Promise<string | null> => {
        try {
            const res = await axios.get(`${BASE()}/connect/${brandId}`, { headers: headers() });
            return res.data?.url ?? null;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            return null;
        }
    }, []);

    // #region generate
    const generate = useCallback(async (
        designId: number,
        brandId: number,
        slotImages: { [propertyName: string]: string }
    ): Promise<SocialPostGeneration | null> => {
        try {
            const res = await axios.post(`${BASE()}/generate`, { designId, brandId, slotImages }, { headers: headers() });
            return res.data as SocialPostGeneration;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            throw err;
        }
    }, []);

    // #region listGenerations
    const listGenerations = useCallback(async (designId: number): Promise<SocialPostGeneration[] | null> => {
        try {
            const res = await axios.get(`${BASE()}/generations/design/${designId}`, { headers: headers() });
            return res.data as SocialPostGeneration[];
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            return null;
        }
    }, []);

    // #region getGeneration
    const getGeneration = useCallback(async (generationId: number): Promise<SocialPostGeneration | null> => {
        try {
            const res = await axios.get(`${BASE()}/generations/${generationId}`, { headers: headers() });
            return res.data as SocialPostGeneration;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            return null;
        }
    }, []);

    // #region updateGeneration
    const updateGeneration = useCallback(async (
        generationId: number,
        brandId: number,
        data: { caption?: string; hashtags?: string; selected_image_id?: number }
    ): Promise<SocialPostGeneration | null> => {
        try {
            const res = await axios.patch(`${BASE()}/generations/${generationId}`, { brandId, ...data }, { headers: headers() });
            return res.data as SocialPostGeneration;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            return null;
        }
    }, []);

    // #region publishGeneration
    const publishGeneration = useCallback(async (generationId: number, brandId: number): Promise<SocialPostGeneration | null> => {
        try {
            const res = await axios.post(`${BASE()}/generations/${generationId}/publish`, { brandId }, { headers: headers() });
            return res.data?.generation as SocialPostGeneration;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            throw err;
        }
    }, []);

    // #region deleteGeneration
    const deleteGeneration = useCallback(async (generationId: number, brandId: number): Promise<boolean> => {
        try {
            await axios.delete(`${BASE()}/generations/${generationId}`, { headers: headers(), data: { brandId } });
            return true;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            throw err;
        }
    }, []);

    // #region getRecentSlotImages
    const getRecentSlotImages = useCallback(async (brandId: number): Promise<SocialSlotRecentImage[] | null> => {
        try {
            const res = await axios.get(`${BASE()}/slot-images/${brandId}`, { headers: headers() });
            return res.data as SocialSlotRecentImage[];
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            return null;
        }
    }, []);

    // #region uploadSlotImage
    const uploadSlotImage = useCallback(async (brandId: number, file: File): Promise<SlotImageUploadResponse | null> => {
        try {
            const base64 = await fileToBase64(file);
            const res = await axios.post(
                `${BASE()}/slot-images/${brandId}`,
                { fileName: file.name, data: base64, contentType: file.type || 'image/png' },
                { headers: headers() }
            );
            return res.data as SlotImageUploadResponse;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            return null;
        }
    }, []);

    // #region getImageSlots
    const getImageSlots = useCallback(async (): Promise<SocialPromptImageSlot[] | null> => {
        try {
            const res = await axios.get(`${BASE()}/slots`, { headers: headers() });
            return res.data as SocialPromptImageSlot[];
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            return null;
        }
    }, []);

    // #region getPromptConfig
    const getPromptConfig = useCallback(async (): Promise<PromptConfigResponse | null> => {
        try {
            const res = await axios.get(`${BASE()}/admin/prompt-config`, { headers: headers() });
            return res.data as PromptConfigResponse;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            return null;
        }
    }, []);

    // #region updatePromptConfig
    const updatePromptConfig = useCallback(async (
        systemPrompt: string,
        captionPrompt: string,
        slots: SocialPromptImageSlot[],
        allowedBrands: number[]
    ): Promise<PromptConfigResponse | null> => {
        try {
            const res = await axios.put(
                `${BASE()}/admin/prompt-config`,
                { system_prompt: systemPrompt, caption_prompt: captionPrompt, slots, allowed_brands: allowedBrands },
                { headers: headers() }
            );
            return res.data as PromptConfigResponse;
        } catch (err: any) {
            errorCatcher(err, logoutRef.current);
            throw err;
        }
    }, []);

    return {
        getConnectionStatus,
        getConnectUrl,
        generate,
        listGenerations,
        getGeneration,
        updateGeneration,
        publishGeneration,
        deleteGeneration,
        getRecentSlotImages,
        uploadSlotImage,
        getImageSlots,
        getPromptConfig,
        updatePromptConfig
    };
};

// #region fileToBase64
function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}
